import { z } from 'zod';
import { ContactChannel, SfObject } from './crm.js';
import { TriageResult } from './review.js';
export const CampaignStatus = z.enum(['draft', 'dry_run', 'active', 'paused', 'archived']);
/** Who is in a campaign: a Salesforce list view (15- or 18-char Id) or a pasted SOQL query. */
export const CampaignSource = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('list_view'), listViewId: z.string().min(15).max(18) }),
    z.object({ kind: z.literal('soql'), soql: z.string().min(10).max(20000) }),
]);
/** True when `days` starts at 0 and every day is later than the one before. */
function isTouchSchedule(days) {
    if (days[0] !== 0)
        return false;
    for (let i = 1; i < days.length; i++) {
        if (days[i] <= days[i - 1])
            return false;
    }
    return true;
}
/** Days after enrollment of each touch, e.g. [0, 1, 3, 6, 10, 14]. */
export const TouchDays = z
    .array(z.number().int().min(0).max(60))
    .min(1)
    .max(12)
    .refine(isTouchSchedule, { message: 'Touch days must start at 0 and strictly increase' });
/** sequence = plan 1A (triage + touch planner); ai_call = plan 1C (picked leads, call plans, AI voice calls). Fixed at creation. */
export const CampaignMode = z.enum(['sequence', 'ai_call']);
/** A 15- or 18-character Salesforce record Id. Shape-checked before it can reach SOQL. */
export const SfRecordIdString = z.string().regex(/^[A-Za-z0-9]{15}(?:[A-Za-z0-9]{3})?$/, 'Not a Salesforce record id');
/** POST /api/campaigns. */
export const CreateCampaignRequest = z.object({
    name: z.string().trim().min(1).max(120),
    sfObject: SfObject,
    source: CampaignSource,
    mode: CampaignMode.default('sequence'),
});
/** PATCH /api/campaigns/:id. */
export const UpdateCampaignRequest = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    refreshMinutes: z.number().int().min(60).max(1440).optional(),
    touchDays: TouchDays.optional(),
});
/** POST /api/campaigns/:id/status. A campaign never returns to draft. */
export const CampaignStatusChange = z.object({ status: z.enum(['dry_run', 'active', 'paused', 'archived']) });
export const Campaign = z.object({
    id: z.string().uuid(),
    name: z.string(),
    sfObject: SfObject,
    source: CampaignSource,
    mode: CampaignMode,
    status: CampaignStatus,
    /** manual | crm_broken | ai_budget | kill_switch while paused; else null. */
    pauseReason: z.string().nullable(),
    /** While paused: what the pause interrupted, so the page can offer the matching resume. Else null. */
    pausedFrom: z.enum(['dry_run', 'active']).nullable(),
    refreshMinutes: z.number(),
    touchDays: z.array(z.number()),
    memberCount: z.number(),
    lastRefreshedAt: z.string().nullable(),
    lastRefreshError: z.string().nullable(),
    createdAt: z.string(),
});
/** GET /api/campaigns. */
export const CampaignsResponse = z.object({ campaigns: z.array(Campaign) });
/** Why a member is not enrolled (preview) or why an enrollment left. */
export const SkipReason = z.enum([
    'no_contact_point',
    'opted_out',
    'blocked',
    'dnc',
    'sf_do_not_call',
    'sf_email_opt_out',
    'skip_on_dialer',
    'in_other_campaign',
    'closed',
]);
export const EnrollmentStatus = z.enum(['active', 'conversing', 'needs_review', 'handed_off', 'completed', 'exited']);
/** GET /api/campaigns/:id/candidates?page=N — the lead picker. */
export const CANDIDATE_PAGE_SIZE = 50;
export const CandidateRecord = z.object({
    sfRecordId: z.string(),
    name: z.string().nullable(),
    ownerName: z.string().nullable(),
    /** The record's AI call consent field as last read; a call still re-checks it in the engine. */
    consentAiCall: z.boolean(),
    /** Why the record would not enroll (null = it can). Null for a record already enrolled here. */
    skipReason: SkipReason.nullable(),
    selected: z.boolean(),
    /**
     * An enrollment of this campaign holds the lead: any status, except an exit with reason
     * `deselected` (that lead is selectable again, and a refresh brings it back).
     */
    enrolled: z.boolean(),
    /** The enrollment's status and exit reason, when one exists (also for an exit that is not `enrolled`). */
    enrollmentStatus: EnrollmentStatus.nullable(),
    exitReason: z.string().nullable(),
});
export const CandidatePage = z.object({
    /** Every member the query returns (capped at 50,000). */
    total: z.number(),
    page: z.number().int().min(1),
    pageSize: z.number(),
    pages: z.number().int().min(1),
    selectedCount: z.number(),
    /** Enrollments of this campaign still `active`: what "Clear" stops at the next refresh. */
    activeEnrolledCount: z.number(),
    records: z.array(CandidateRecord).max(CANDIDATE_PAGE_SIZE),
});
/** PUT /api/campaigns/:id/selection. `selectAll` selects every current member; `clear` deselects everything. */
export const SELECTION_CHANGE_MAX = 500;
export const SelectionChange = z
    .object({
    add: z.array(SfRecordIdString).max(SELECTION_CHANGE_MAX).default([]),
    remove: z.array(SfRecordIdString).max(SELECTION_CHANGE_MAX).default([]),
    selectAll: z.boolean().default(false),
    clear: z.boolean().default(false),
})
    .refine((c) => c.add.length > 0 || c.remove.length > 0 || c.selectAll || c.clear, { message: 'Nothing to change' });
export const SelectionResponse = z.object({
    selectedCount: z.number(),
    /** Ids in `add` that are not members of the campaign's query (never selected). */
    ignored: z.number(),
});
/** POST /api/campaigns/preview. */
export const PreviewRequest = z.object({ sfObject: SfObject, source: CampaignSource });
export const PreviewRecord = z.object({
    sfRecordId: z.string(),
    name: z.string().nullable(),
    ownerName: z.string().nullable(),
    channels: z.array(ContactChannel),
    skipReason: SkipReason.nullable(),
});
export const CampaignPreview = z.object({
    /** Every member Id the query returns. */
    total: z.number(),
    /** Members whose fields were checked (the first 2,000 at most). */
    examined: z.number(),
    /** Of `examined`. */
    eligible: z.number(),
    /** Of `examined`, by reason. */
    skipped: z.record(SkipReason, z.number()),
    /** The first 20 examined. */
    sample: z.array(PreviewRecord).max(20),
});
export const TouchChannel = z.enum(['ai_call', 'rep_call', 'sms', 'email']);
export const TouchStatus = z.enum(['planned', 'held', 'queued', 'dialing', 'sent', 'failed', 'skipped']);
/** One planner rule's verdict on one channel — a touch's gate audit is a list of these. */
export const GateStep = z.object({
    rule: z.string(),
    channel: z.string(),
    verdict: z.enum(['removed', 'deferred', 'kept', 'held']),
    detail: z.string(),
});
/** One enrollment in the campaign's plan view. Triage is shown without its do-not-contact quote. */
export const PlanRow = z.object({
    enrollmentId: z.string().uuid(),
    sfRecordId: z.string(),
    name: z.string().nullable(),
    ownerName: z.string().nullable(),
    status: EnrollmentStatus,
    exitReason: z.string().nullable(),
    triage: TriageResult.omit({ doNotContact: true }).nullable(),
    nextTouch: z
        .object({
        seq: z.number(),
        channel: TouchChannel,
        status: TouchStatus,
        dueAt: z.string(),
        gateAudit: z.array(GateStep),
    })
        .nullable(),
});
/** GET /api/campaigns/:id/plan — 50 rows a page; `counts` covers the whole campaign. */
export const CampaignPlanResponse = z.object({
    rows: z.array(PlanRow),
    nextCursor: z.string().nullable(),
    counts: z.record(EnrollmentStatus, z.number()),
});
//# sourceMappingURL=campaigns.js.map